import React from 'react'
import { useTranslation } from 'react-i18next'

import Banner from '@/components-latest/primitives/Banner'
import Button from '@/components-latest/primitives/Button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components-latest/primitives/Collapsible'
import Checkbox from '@/components-latest/primitives/Controls/Checkbox'
import TimePicker from '@/components-latest/primitives/Controls/TimePicker'
import {
  ArrowDown,
  InfoCircle,
  Minus,
  Plus
} from '@/components-latest/primitives/Icons'
import Label from '@/components-latest/primitives/Label'
import Separator from '@/components-latest/primitives/Separator'
import Text from '@/components-latest/primitives/Text'
import { Form, FormControl, FormField } from '@/components-latest/shared/Form'
import { useBusinessHoursForm } from '@/components-latest/ui/AccountPage/BusinessHoursForm/useBusinessHoursForm'
import { ApiGetBusinessHours } from '@/ducks/account/account.types'

import Styles from './BusinessHoursForm.module.scss'

interface IBusinessFormProps {
  initialData: ApiGetBusinessHours
}

const BusinessHoursForm: React.FC<IBusinessFormProps> = ({ initialData }) => {
  const { t } = useTranslation()

  const {
    form,
    handleDeleteSlot,
    handleAddSlot,
    hasAdditionalSlot,
    handleSubmit,
    getFormattedValue,
    shouldDisableAdd,
    getMixMaxRules,
    errorMessage,
    errorHours
  } = useBusinessHoursForm(initialData)

  return (
    <div className={Styles.content}>
      <Separator variant="solid" />
      <Text variant="nextSubtitle">
        {t('account.business-hours.description')}
      </Text>
      {errorMessage && errorHours && (
        <Banner
          className={Styles.errorBanner}
          state="negative"
          type="boxed"
          description={t('account.business-hours.invalid.title')}
          showList
          errorList={Array.isArray(errorHours) ? errorHours : []}
          icon={<InfoCircle />}
        />
      )}
      <Form {...form}>
        <form className={Styles.form} onSubmit={handleSubmit}>
          {form.getValues().businessHours.map((weekdayValues, index) => (
            <div className={Styles.itemWrapper} key={weekdayValues.day_name}>
              <Collapsible className={Styles.collapsible}>
                <div className={Styles.collapsibleHeader}>
                  <div className={Styles.collapsibleControl}>
                    <div>
                      <FormField
                        name={`businessHours.${index}.isWorking`}
                        control={form.control}
                        render={({ field }) => {
                          return (
                            <Label
                              layout="row"
                              className={Styles.checkboxLabel}>
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={(checked: boolean) =>
                                    field.onChange(checked)
                                  }
                                />
                              </FormControl>
                              <Text
                                variant="nextSubtitle"
                                className={Styles.checkboxText}>
                                {t(`common.weekdays.${weekdayValues.day_name}`)}
                                <Text
                                  variant="nextBody"
                                  asChild
                                  className={Styles.checkboxValue}>
                                  <span>
                                    {getFormattedValue(weekdayValues)}
                                  </span>
                                </Text>
                              </Text>
                            </Label>
                          )
                        }}
                      />
                    </div>
                  </div>
                  <CollapsibleTrigger asChild>
                    <button className={Styles.trigger}>
                      <ArrowDown className={Styles.triggerIcon} />
                    </button>
                  </CollapsibleTrigger>
                </div>
                <CollapsibleContent>
                  <div className={Styles.itemContent}>
                    <div className={Styles.timeItem}>
                      <FormField
                        name={`businessHours.${index}.start_time`}
                        control={form.control}
                        render={({ field }) => {
                          return (
                            <Label layout="column" className={Styles.timeLabel}>
                              <Text variant="nextBody" weight="semiBold">
                                {t('account.business-hours.time-label-start')}
                              </Text>
                              <TimePicker
                                {...field}
                                {...getMixMaxRules(index, 'A')}
                              />
                            </Label>
                          )
                        }}
                      />

                      <FormField
                        name={`businessHours.${index}.end_time`}
                        control={form.control}
                        render={({ field }) => {
                          return (
                            <Label layout="column" className={Styles.timeLabel}>
                              <Text variant="nextBody" weight="semiBold">
                                {t('account.business-hours.time-label-end')}
                              </Text>
                              <TimePicker
                                {...field}
                                {...getMixMaxRules(index, 'B')}
                              />
                            </Label>
                          )
                        }}
                      />
                    </div>

                    {hasAdditionalSlot(index) && (
                      <div className={Styles.timeItem}>
                        <FormField
                          name={`businessHours.${index}.slots.0.start_time`}
                          control={form.control}
                          render={({ field }) => {
                            return (
                              <Label
                                layout="column"
                                className={Styles.timeLabel}>
                                <Text variant="nextBody" weight="semiBold">
                                  {t('account.business-hours.time-label-start')}
                                </Text>
                                <TimePicker
                                  {...field}
                                  {...getMixMaxRules(index, 'C')}
                                />
                              </Label>
                            )
                          }}
                        />

                        <FormField
                          name={`businessHours.${index}.slots.0.end_time`}
                          control={form.control}
                          render={({ field }) => {
                            return (
                              <Label
                                layout="column"
                                className={Styles.timeLabel}>
                                <Text variant="nextBody" weight="semiBold">
                                  {t('account.business-hours.time-label-end')}
                                </Text>
                                <TimePicker
                                  {...field}
                                  {...getMixMaxRules(index, 'D')}
                                />
                              </Label>
                            )
                          }}
                        />
                      </div>
                    )}
                    {!hasAdditionalSlot(index) ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="medium"
                        icon={<Plus />}
                        onClick={
                          !shouldDisableAdd(index)
                            ? () => handleAddSlot(index)
                            : () => {}
                        }
                        disabled={shouldDisableAdd(index)}>
                        {t('account.business-hours.add-slot')}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="destructive"
                        size="medium"
                        icon={<Minus />}
                        onClick={() => handleDeleteSlot(index)}>
                        {t('account.business-hours.delete-slot')}
                      </Button>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          ))}
          <Button
            variant="primary"
            size="medium"
            className={Styles.submit}
            disabled={!form.formState.isDirty}>
            {t('account.business-hours.submit')}
          </Button>
        </form>
      </Form>
    </div>
  )
}

export default BusinessHoursForm
